import type { AccessPolicy, Extension, Patient, Practitioner } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import { useEffect, useMemo, useState } from 'react';
import type { Role } from '../../users/types/rbac';
import type { User } from '../../users/types/rbac';
import {
  SETTINGS_REFERENCE_ROLES,
  getAccessPolicyForRole,
  buildRolePermissionsViewModel,
  type RolePermissionsViewModel,
} from '@/features/rbac/rolePermissionsView';
import { EXTENSION_URLS } from '@/features/rbac/constants';

export type UiPermissionSource = {
  extension?: Extension[];
  resourceType?: string;
  id?: string;
};

export function useAccessPolicies(): {
  policies: AccessPolicy[];
  loading: boolean;
  error: string | null;
} {
  const medplum = useMedplum();
  const [policies, setPolicies] = useState<AccessPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        medplum.invalidateSearches('AccessPolicy');
        const found = await medplum.searchResources('AccessPolicy', { _count: '100' });
        if (!cancelled) {
          setPolicies(found);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : String(err);
          // Staff/provider sessions may be forbidden from listing AccessPolicy resources.
          // Keep the view functional via canonical role->policy mapping fallback.
          if (/forbidden/i.test(message)) {
            setPolicies([]);
            setError(null);
          } else {
            setError(message);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [medplum]);

  return { policies, loading, error };
}

/**
 * Load ui-permissions extension sources from directory sample users (one per role).
 * Falls back to AccessPolicy extension when present on the mapped policy.
 */
export function useRoleUiPermissionSources(
  users: User[],
  policies: AccessPolicy[],
): {
  sourcesByRole: Partial<Record<Role, UiPermissionSource>>;
  loading: boolean;
} {
  const medplum = useMedplum();
  const [sourcesByRole, setSourcesByRole] = useState<Partial<Record<Role, UiPermissionSource>>>({});
  const [loading, setLoading] = useState(false);

  const sampleUserIds = useMemo(() => {
    const map: Partial<Record<Role, string>> = {};
    for (const role of SETTINGS_REFERENCE_ROLES) {
      const match = users.find((user) => user.role === role);
      if (match) map[role] = match.id;
    }
    return map;
  }, [users]);

  useEffect(() => {
    let cancelled = false;
    const rolesToFetch = SETTINGS_REFERENCE_ROLES.filter((role) => sampleUserIds[role]);
    if (rolesToFetch.length === 0) {
      setSourcesByRole({});
      setLoading(false);
      return;
    }

    setLoading(true);
    void (async () => {
      const next: Partial<Record<Role, UiPermissionSource>> = {};

      for (const role of SETTINGS_REFERENCE_ROLES) {
        const policy = getAccessPolicyForRole(role, policies);
        if (policy?.extension?.some((entry) => entry.url === EXTENSION_URLS.uiPermissions)) {
          next[role] = {
            extension: policy.extension,
            resourceType: 'AccessPolicy',
            id: policy.id,
          };
        }
      }

      await Promise.all(
        rolesToFetch.map(async (role) => {
          const userId = sampleUserIds[role];
          if (!userId || next[role]) return;
          const resourceType = role === 'client' ? 'Patient' : 'Practitioner';
          try {
            const resource = await medplum.readResource(
              resourceType,
              userId,
            ) as Patient | Practitioner;
            if (!cancelled) {
              next[role] = {
                extension: resource.extension,
                resourceType: resource.resourceType,
                id: resource.id,
              };
            }
          } catch {
            // Non-blocking: role tab will show missing-extension warning.
          }
        }),
      );

      if (!cancelled) {
        setSourcesByRole(next);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [medplum, policies, sampleUserIds]);

  return { sourcesByRole, loading };
}

export function useRolePermissionsViewModel(
  role: Role,
  policies: AccessPolicy[],
  uiSource?: UiPermissionSource,
): RolePermissionsViewModel {
  return useMemo(
    () => buildRolePermissionsViewModel(role, policies, uiSource),
    [role, policies, uiSource],
  );
}

/** Load the FHIR profile resource for a directory user (Patient or Practitioner). */
export function useProfileResourceForUser(
  userId: string | undefined,
  role: Role | undefined,
  refreshToken = 0,
): {
  source?: UiPermissionSource;
  loading: boolean;
} {
  const medplum = useMedplum();
  const [source, setSource] = useState<UiPermissionSource | undefined>();
  const [loading, setLoading] = useState(() => Boolean(userId && role));

  useEffect(() => {
    if (!userId || !role) {
      setSource(undefined);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setSource(undefined);
    setLoading(true);
    const resourceType = role === 'client' ? 'Patient' : 'Practitioner';
    medplum.invalidateSearches(resourceType);
    void medplum
      .readResource(resourceType, userId, { cache: 'no-cache' })
      .then((resource) => {
        if (!cancelled) {
          setSource({
            extension: resource.extension,
            resourceType: resource.resourceType,
            id: resource.id,
          });
        }
      })
      .catch(() => {
        if (!cancelled) setSource(undefined);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [medplum, userId, role, refreshToken]);

  return { source, loading };
}
